import React, { useState, useEffect } from "react";
import Footer from "@/components/Layout/Footer";
import { usePrivy } from "@privy-io/react-auth";
import { Box, Fade } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useAppSelector } from "@/redux/hooks";
import SpinnerLoading from "@/components/SpinnerLoading";
import Navigation from "@/components/Layout/Navbar";
import AutoLogout from "../AutoLogout";
import WhitelistCheckin from "../WhiteList";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { ready, authenticated, user } = usePrivy();
  const router = useRouter();
  const isVerified = useAppSelector((state) => state.verify.isVerified);
  const pathname = router.state.location.pathname;
  const walletAddress = user?.wallet?.address || "";
  const [isLoading, setIsLoading] = useState(true);

  // Store login preference when user authenticates
  useEffect(() => {
    if (ready && authenticated && user) {
      const loginMethod = localStorage.getItem("preferredLoginMethod");

      // If user logged in with Google, store that preference
      if (user.google && (!loginMethod || loginMethod !== "google")) {
        localStorage.setItem("preferredLoginMethod", "google");
      }
      // If user logged in with wallet and no preference is set, store that preference
      else if (user.wallet && !loginMethod) {
        localStorage.setItem("preferredLoginMethod", "wallet");
      }
    }
  }, [ready, authenticated, user]);

  // Handle verification and navigation based on auth state
  useQuery({
    queryKey: ["authNavigation", authenticated, isVerified, pathname],
    queryFn: async () => {
      if (!ready) return null;
      setIsLoading(true);
      const privyToken = localStorage.getItem("privy:token");
      try {
        if (authenticated && !isVerified && privyToken) {
          // User is authenticated but not verified - redirect to verification
          router.navigate({ to: "/verify" });
        }
        return null;
      } catch (error) {
        console.error("Navigation error:", error);
        return null;
      } finally {
        // Don't hide loading yet - let the page transition handle it
        // Only hide if there's an error
        if (!pathname) setIsLoading(false);
      }
    },
    enabled: ready,
    refetchOnWindowFocus: false,
  });

  // Handle page transitions with shorter delay
  useQuery({
    queryKey: ["pageTransition", pathname],
    queryFn: async () => {
      setIsLoading(true);
      return new Promise(
        (resolve) =>
          setTimeout(() => {
            setIsLoading(false);
            resolve(null);
          }, 800) // Reduced from 3000ms to 800ms for better user experience
      );
    },
    enabled: Boolean(pathname),
  });

  // Handle loading state
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000); // Reduced from 3000ms to 800ms for better user experience
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: "auto",
        height: "100dvh",
        maxWidth: "max-content",
        bgcolor: "black",
      }}
      className="flex flex-col min-h-screen min-w-screen"
    >
      <Navigation />
      <main className="flex-grow relative">
        {walletAddress ? (
          // <WhitelistCheckin address={walletAddress}>
          <AutoLogout>{children}</AutoLogout>
        ) : (
          // </WhitelistCheckin>
          <AutoLogout>{children}</AutoLogout>
        )}
        <div
          className="from-black via-40% to-[#1e1e1e] bg-gradient-to-b border-b-2 solid border-(--gray-8)"
          style={{
            backdropFilter: "blur(4px)",
            position: "relative",
            width: "100%",
            height: "120px",
            zIndex: 1,
            bottom: 0,
          }}
        />
      </main>
      <Footer />

      {/* Loading Overlay */}
      <Fade in={isLoading}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <SpinnerLoading size={100} />
        </Box>
      </Fade>
    </Box>
  );
}
